import Cookies from "js-cookie";
import * as React from "react";
import { v4 as uuid } from "uuid";

import { LeadFormValues } from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import { TrackSignupSuccess } from "@brexhq/next/src/hooks/useAnalytics";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import {
  useSubmit,
  FormValues,
  getUserProperties,
  genericErrorBanner,
  unableToCreateAccountBanner,
  getDashboardRedirectUrl,
  PartnerRefValues,
  getLeadProperties,
} from "@brexhq/next/src/routes/signup/Form";
import { usePreviewEnvironment } from "@brexhq/next/src/routes/signup/hooks";
import {
  logAnalyticsError,
  logSignupFailedError,
} from "@brexhq/next/src/telemetry/logging";
import { getSavedMarketingAttributionProperties } from "@brexhq/next/src/utils/analytics";
import {
  getApiEndpoint,
  getRedirectUrl,
} from "@brexhq/next/src/utils/endpoints";
import { parseQueryString } from "@brexhq/next/src/utils/parseQueryString";
import { createMockableHook } from "@brexhq/shared/utils/createMockableHook";
import { isDev } from "@brexhq/shared/utils/env/isDevEnv";

const signupQuery = `
    mutation softSignupMutation($input: SignupInput!) {
      signup(input: $input) {
        userId
        token
      }
    }
  `;

/**
 * Have a function to get refcode based on the attributes.
 * We want to detail better what kind of ref codes are being sent
 * as opposed to the generic "attributionToken" and "selfAtteseted"
 */
const getRefCodes = (
  location: Window["location"],
  partnerRefToken: string | undefined,
) => {
  /**
   * Fixed lengths for the respective type of ref code
   */
  const PARTNER_REF_TOKEN_LENGTH = 14;
  const USER_REF_TOKEN_LENGTH = 7;
  /**
   * If undefined, we can default to empty strings.
   * Another check to get the values directly from urlParams (if exists)
   * since historically there were instances with users blocking cookies
   */
  const ref_code =
    Cookies.get("ref_code") || parseQueryString("ref_code", location) || "";
  const rc = Cookies.get("rc") || parseQueryString("rc", location) || "";

  /**
   * set them all originally as null to conditionally
   * add values that apply to them using the two cookies above.
   */
  let ads: string | null = null;
  let generalPartner: string | null = null;
  let onboardingApiPartner: string | null = null;
  let firstParty: string | null = null;

  /**
   * ads refcode logic
   */
  if (ref_code.startsWith("pmk_")) {
    ads = ref_code;
  }

  /**
   * Right now no distinct way to determine general
   * partner ref codes. Going to assign every time.
   */
  generalPartner = ref_code;

  /**
   * Logic for onboarding api's partner referral token.
   * must be 14 digits long alphanumeric string.
   *
   * Alternatively, if a partner referral token already exists
   * make that value take precedence
   */
  if (ref_code.length === PARTNER_REF_TOKEN_LENGTH || partnerRefToken) {
    onboardingApiPartner = partnerRefToken || ref_code;
  }

  /**
   * First party ref code logic
   */
  const isUserRef = rc.length === USER_REF_TOKEN_LENGTH;
  const isEmailRef = rc.startsWith("c");

  if (isUserRef || isEmailRef) {
    firstParty = rc;
  }

  return {
    ads,
    generalPartner,
    onboardingApiPartner,
    firstParty,
  };
};

const _useSoftSignup = ({
  formValues,
  trackEarlyAccountSignupSuccess,
  anonymousId,
  partnerRef,
}: {
  partnerRef?: PartnerRefValues | null;
  formValues: FormValues;
  trackEarlyAccountSignupSuccess: TrackSignupSuccess;
  anonymousId: string;
}) => {
  const _useSubmit = useSubmit();
  const isSubmitting = _useSubmit.isSubmitting;
  const setIsSubmitting = _useSubmit.setIsSubmitting;
  const setSubmitError = _useSubmit.setSubmitError;
  const submitError = _useSubmit.submitError;

  const { previewEnvironmentNamespace } = usePreviewEnvironment();
  const location = useLocation();

  const submitUserData = async () => {
    setIsSubmitting(true);
    const userProperties = getUserProperties(formValues);
    const firstName = userProperties.firstName;
    const lastName = userProperties.lastName;
    const email = userProperties.email;
    const password = userProperties.password;
    const selfAttestedRefCode = userProperties.selfAttestedRefCode;
    const attributionMetadata = userProperties.attributionMetadata;
    const attributionToken = userProperties.attributionToken;
    const companyName = userProperties.companyName;
    const platformAgreement = userProperties.platformAgreement;
    const howDidYouHear = userProperties.howDidYouHear;
    const leadToken = userProperties.leadToken;

    const apiEndpoint: string = getApiEndpoint();
    const redirectUrl: string = getRedirectUrl();

    // These need to be kept in sync manually for now.
    // Definitely not ideal. Could potentially use frontmatter in md and query them,
    // or potentially move them to a cms where they can be updated self-serve by
    // legal team, and updated time will be part of metadata.
    const legalAgreements = [
      // This date needs to be synced to the date here:
      // https://github.com/brexhq/tm-landing/blob/cash-ga-soft-signup/src/data/BrexUserAgmt.md#brex-user-agreement
      { name: "userAgreement", version: "06.06.2018" },
      // This date needs to be synced to the date here:
      // https://github.com/brexhq/tm-landing/blob/cash-ga-soft-signup/src/data/BrexPrivacyPolicy.md#brex-privacy-policy
      { name: "privacyPolicy", version: "09.19.2019" },
    ];

    if (platformAgreement) {
      legalAgreements.push({
        name: "platformAgreement",
        version: "2020.03.31",
      });
    }

    const rawSavedMarketingAttributionProperties =
      getSavedMarketingAttributionProperties();

    const savedMarketingAttributionProperties = {
      ...rawSavedMarketingAttributionProperties,
      ref_code:
        // We treat rc as if it's a ref_code param
        // This is used for referral links in the format
        // brex.com/signup?rc=whatever
        // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
        rawSavedMarketingAttributionProperties.ref_code ||
        rawSavedMarketingAttributionProperties.rc,
    };

    try {
      const signupResponse = await fetch(apiEndpoint, {
        method: "POST",
        body: JSON.stringify({
          query: signupQuery,
          variables: {
            input: {
              clientMutationId: uuid(),
              email,
              password,
              redirectUrl,
              firstName,
              lastName,
              companyName,
              blackbox: (window as any).iovation_blackbox,
              legalAgreements,
              marketingSourceMetadata: JSON.stringify({
                self_attested_ref_code: selfAttestedRefCode || undefined,
                ...savedMarketingAttributionProperties,
                softSignupFromWebsite: true,
                howDidYouHear,
              }),
              attributionMetadata: {
                /**
                 * we don't want to change the original shape of attributionMetaData
                 * e.g. fbc, gclid, fbp for consistency purposes
                 */
                ...attributionMetadata,
                segmentAnonymousId: anonymousId,
              },
              referralCodes: {
                ...getRefCodes(location, partnerRef?.token),
                selfAttested: selfAttestedRefCode,
                finalAttribution: attributionToken,
              },
              leadToken,
            },
          },
        }),
        headers: new Headers({ "Content-Type": "application/json" }),
      });

      if (!signupResponse.ok) {
        setIsSubmitting(false);
        setSubmitError(genericErrorBanner);
        logSignupFailedError(
          new Error(`Signup network request failed for ${anonymousId}`),
        );
        return;
      }

      const signupResponseData = await signupResponse.json();

      if (signupResponseData.errors) {
        setIsSubmitting(false);
        const isInternalError = signupResponseData.errors.some(
          (error: { message: string }) =>
            error.message === "Input failed validations",
        );

        // we infer this to mean the user's email already exists
        // we don't give out that information explicitly due to privacy concerns
        if (isInternalError) {
          setSubmitError(unableToCreateAccountBanner(email));
          return;
        } else {
          setSubmitError(genericErrorBanner);
        }
        logSignupFailedError(
          new Error(`Signup mutation failed for ${anonymousId}`),
        );
        return;
      }

      const user = signupResponseData?.data?.signup;

      try {
        const analyticsUserProperties = {
          ...(selfAttestedRefCode && {
            self_attested_ref_code: selfAttestedRefCode,
          }),
          ...savedMarketingAttributionProperties,
          softSignupFromWebsite: true,
          anonymousId,
          // Flag not being gated for the leadToken value here, we want to use this for analytics.
          leadToken: userProperties.leadToken,
          email,
        };

        trackEarlyAccountSignupSuccess(user?.userId, analyticsUserProperties);
      } catch (error) {
        // swallow analytics errors. these should never block signup
        if (error instanceof Error) {
          logAnalyticsError(error);
        }
      }
      const token = signupResponseData?.data?.signup?.token;
      const userId = signupResponseData?.data?.signup?.userId;

      const session = {
        userId, // "userId" and "token" are here to maintain the previous shape of the "session" object that was passed to the dashboard.
        token,
        isJWT: true, // this is added here for dashboard to decide which authentication flow will it use.
      };

      const shouldUsePreviewEnv = !!(isDev && previewEnvironmentNamespace);

      const dashboardRedirectUrl = getDashboardRedirectUrl(
        session,
        shouldUsePreviewEnv,
        previewEnvironmentNamespace,
      );

      setIsSubmitting(false);
      window.location.href = dashboardRedirectUrl.href;
    } catch (error) {
      setIsSubmitting(false);

      setSubmitError(genericErrorBanner);
      if (error instanceof Error) {
        logSignupFailedError(error);
      }
    }
  };

  /**
   * @param formValues M7 Lead form using signup
   */
  const submitUserDataWithFormValue = async (formValues: LeadFormValues) => {
    setIsSubmitting(true);

    const userProperties = getLeadProperties(formValues);
    const {
      firstName,
      lastName,
      email,
      password,
      selfAttestedRefCode,
      attributionMetadata,
      attributionToken,
      companyName,
      leadToken,
      companyType,
      employeeCount,
      fundingSources,
      salesVolume,
      industry,
      website,
      salesSegment,
    } = userProperties;

    const apiEndpoint: string = getApiEndpoint();
    const redirectUrl: string = getRedirectUrl();

    // These need to be kept in sync manually for now.
    // Definitely not ideal. Could potentially use frontmatter in md and query them,
    // or potentially move them to contentful where they can be updated self-serve by
    // legal team, and updated time will be part of metadata.
    const legalAgreements = [
      // This date needs to be synced to the date here:
      // https://github.com/brexhq/tm-landing/blob/cash-ga-soft-signup/src/data/BrexUserAgmt.md#brex-user-agreement
      { name: "userAgreement", version: "06.06.2018" },
      // This date needs to be synced to the date here:
      // https://github.com/brexhq/tm-landing/blob/cash-ga-soft-signup/src/data/BrexPrivacyPolicy.md#brex-privacy-policy
      { name: "privacyPolicy", version: "09.19.2019" },
      {
        name: "platformAgreement",
        version: "2020.03.31",
      },
    ];

    const rawSavedMarketingAttributionProperties =
      getSavedMarketingAttributionProperties();

    const savedMarketingAttributionProperties = {
      ...rawSavedMarketingAttributionProperties,
      ref_code:
        // We treat rc as if it's a ref_code param
        // This is used for referral links in the format
        // brex.com/signup?rc=whatever
        // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
        rawSavedMarketingAttributionProperties.ref_code ||
        rawSavedMarketingAttributionProperties.rc,
    };

    try {
      const signupResponse = await fetch(apiEndpoint, {
        method: "POST",
        body: JSON.stringify({
          query: signupQuery,
          variables: {
            input: {
              clientMutationId: uuid(),
              info: {
                v1: {
                  companyType,
                  employeeCount,
                  fundingSources,
                  salesVolume,
                },
              },
              email,
              industry,
              password,
              redirectUrl,
              companyWebsite: website,
              firstName,
              lastName,
              companyName,
              blackbox: (window as any).iovation_blackbox,
              legalAgreements,
              marketingSourceMetadata: JSON.stringify({
                self_attested_ref_code: selfAttestedRefCode || undefined,
                ...savedMarketingAttributionProperties,
                softSignupFromWebsite: true,
              }),
              attributionMetadata: {
                /**
                 * we don't want to change the original shape of attributionMetaData
                 * e.g. fbc, gclid, fbp for consistency purposes
                 */
                ...attributionMetadata,
                segmentAnonymousId: anonymousId,
              },
              referralCodes: {
                ...getRefCodes(location, partnerRef?.token),
                selfAttested: selfAttestedRefCode,
                finalAttribution: attributionToken,
              },
              leadToken,
            },
          },
        }),
        headers: new Headers({ "Content-Type": "application/json" }),
      });

      if (!signupResponse.ok) {
        logSignupFailedError(
          new Error(`Signup network request failed for ${anonymousId}`),
        );
        return Promise.reject({
          title: "Something went wrong",
          message: (
            <>
              Please refresh your page to try again. If the problem persists,
              contact <a href="mailto:concierge@brex.com">concierge@brex.com</a>
              .
            </>
          ),
        });
      }

      const signupResponseData = await signupResponse.json();
      if (signupResponseData.errors) {
        setIsSubmitting(false);
        const isInternalError = signupResponseData.errors.some(
          (error: { message: string }) =>
            error.message === "Input failed validations",
        );

        // we infer this to mean the user's email already exists
        // we don't give out that information explicitly due to privacy concerns
        if (isInternalError) {
          return Promise.reject({
            title: "Unable to create account",
            message: (
              <div className="_lr-hide dd-privacy-mask">
                We were unable to create your account with email {email}. Please
                check your email for next steps, otherwise use a different email
                address to sign up.
              </div>
            ),
          });
        }
        logSignupFailedError(
          new Error(`Signup mutation failed for ${anonymousId}`),
        );
        return Promise.reject({
          title: "Something went wrong",
          message: (
            <>
              Please refresh your page to try again. If the problem persists,
              contact <a href="mailto:concierge@brex.com">concierge@brex.com</a>
              .
            </>
          ),
        });
      }

      const user = signupResponseData?.data?.signup;

      try {
        const analyticsUserProperties = {
          ...(selfAttestedRefCode && {
            self_attested_ref_code: selfAttestedRefCode,
          }),
          ...savedMarketingAttributionProperties,
          softSignupFromWebsite: true,
          anonymousId,
          // Flag not being gated for the leadToken value here, we want to use this for analytics.
          leadToken: userProperties.leadToken,
        };

        trackEarlyAccountSignupSuccess(user?.userId, {
          ...analyticsUserProperties,
          email,
          companyName,
          companyType,
          employeeCount,
          fundingSources,
          salesVolume,
          industry,
          website,
          salesSegment,
        });
      } catch (error) {
        // swallow analytics errors. these should never block signup
        console.log(error);
        if (error instanceof Error) {
          logAnalyticsError(error);
        }
      }
      const token = signupResponseData?.data?.signup?.token;
      const userId = signupResponseData?.data?.signup?.userId;

      const session = {
        userId, // "userId" and "token" are here to maintain the previous shape of the "session" object that was passed to the dashboard.
        token,
        isJWT: true, // this is added here for dashboard to decide which authentication flow will it use.
      };

      const shouldUsePreviewEnv = !!(isDev && previewEnvironmentNamespace);

      const dashboardRedirectUrl = getDashboardRedirectUrl(
        session,
        shouldUsePreviewEnv,
        previewEnvironmentNamespace,
      );

      window.location.href = dashboardRedirectUrl.href;
      return;
    } catch (error) {
      if (error instanceof Error) {
        logSignupFailedError(error);
      }
      return Promise.reject({
        title: "Something went wrong",
        message: (
          <>
            Please refresh your page to try again. If the problem persists,
            contact <a href="mailto:concierge@brex.com">concierge@brex.com</a>.
          </>
        ),
      });
    }
  };

  return {
    submitUserData,
    isSubmitting,
    submitError,
    submitUserDataWithFormValue,
  };
};

export const [useSoftSignup, MockUseSoftSignup] =
  createMockableHook(_useSoftSignup);
